// # Samples page component
import React, { useEffect, useRef, useState } from 'react';
import DashboardHeader from '../../components/DashboardHeader';
import Header from '../components/header';  // # Import regular header
import { useAuthStore } from '../../auth/state';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import cacheManager from '../../auth/cache_manager';
import './samples.css';



function Samples() {
  // # Create ref for iframe
  const iframeRef = useRef(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user, isAuthenticated } = useAuthStore();  // # Get user and auth state
  
  // # Get iframe URL from environment variables - SECURITY: No direct URL exposure
  const iframeUrl = process.env.REACT_APP_SAMPLES_IFRAME_URL;

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }

        // # Update iframe theme if it exists
        if (iframeRef.current) {
          try {
            // # Update iframe data-theme attribute
            iframeRef.current.setAttribute('data-theme', newTheme ? 'dark' : 'light');
            
            // # Update iframe style
            iframeRef.current.style.colorScheme = newTheme ? 'dark' : 'light';
            iframeRef.current.style.backgroundColor = newTheme ? '#121212' : 'white';
            
            // # Attempt to inject theme styles into the iframe
            setTimeout(() => {
              try {
                if (iframeRef.current && iframeRef.current.contentDocument) {
                  const style = document.createElement('style');
                  if (newTheme) {
                    // # Dark mode styles
                    style.textContent = `
                      body, html {
                        background-color: #121212 !important;
                        color-scheme: dark !important;
                      }
                      
                      /* # Dark mode text colors */
                      body {
                        color: #e0e0e0 !important;
                      }
                      
                      /* # Dark mode link colors */
                      a {
                        color: #3498db !important;
                      }
                      
                      /* # Dark mode background elements */
                      .notion-page-content {
                        background-color: #121212 !important;
                      }
                      
                      /* # Dark mode borders */
                      [style*="border"] {
                        border-color: #333333 !important;
                      }
                    `;
                  } else {
                    // # Light mode styles
                    style.textContent = `
                      body, html {
                        background-color: white !important;
                        color-scheme: light !important;
                      }
                    `;
                  }
                  iframeRef.current.contentDocument.head.appendChild(style);
                }
              } catch (e) {
                // # Silently fail if cross-origin issues prevent access
              }
            }, 1000);
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };
  
  // # Function to handle iframe load events - defined outside useEffect to be accessible to onLoad prop
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        // # Try to access iframe content to inject error suppression and theme
        const style = document.createElement('style');
        style.textContent = `
          /* Hide error messages and UI elements that might expose URLs */
          .notion-overlay-container, 
          .notion-login-prompt,
          .notion-help-button,
          .notion-cursor-listener,
          .notion-frame > div[style*="fixed"],
          .notion-frame > div[style*="absolute"],
          .notion-topbar,
          .notion-selectable[data-block-id],
          .notion-link-token,
          .notion-page-content [href],
          .notion-collection-card__content,
          .notion-header__icon-wrapper,
          audio, video {
            display: none !important;
          }
          
          /* Hide all error messages */
          [class*="error"],
          [class*="Error"],
          [class*="warning"],
          [class*="Warning"] {
            display: none !important;
          }

          /* Apply current theme */
          body, html {
            background-color: ${isDarkMode ? '#121212' : 'white'} !important;
            color-scheme: ${isDarkMode ? 'dark' : 'light'} !important;
          }
          
          ${isDarkMode ? `
            /* Dark mode styles */
            body {
              color: #e0e0e0 !important;
            }
            
            a {
              color: #3498db !important;
            }
            
            .notion-page-content {
              background-color: #121212 !important;
            }
            
            [style*="border"] {
              border-color: #333333 !important;
            }
          ` : ''}
        `;
        
        // # Attempt to inject the style into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              iframeRef.current.contentDocument.head.appendChild(style);
              
              // # Also try to intercept console methods in the iframe
              const script = document.createElement('script');
              script.textContent = `
                // Override console methods in iframe
                console.error = function() {};
                console.warn = function() {};
                console.log = function() {};
                console.info = function() {};
                
                // Block network requests that might leak URLs
                const originalFetch = window.fetch;
                window.fetch = function(url, options) {
                  if (url.includes('notion.site') || url.includes('http')) {
                    return new Promise(() => {});
                  }
                  return originalFetch.apply(this, arguments);
                };
              `;
              iframeRef.current.contentDocument.head.appendChild(script);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };
  
  // # Add effect to handle iframe loading and errors
  useEffect(() => {
    // # Override console methods specifically for iframe-related errors
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;
    const originalConsoleLog = console.log;
    
    // # Create a more aggressive filter for URL-exposing messages
    const filterUrlMessages = (args) => {
      if (!args || args.length === 0) return false;
      
      const firstArg = args[0];
      
      // # Check if it's a string that might contain a URL
      if (typeof firstArg === 'string') {
        // # Check for common URL patterns or specific domains
        if (firstArg.includes('notion.site') || 
            firstArg.includes('http') || 
            firstArg.includes('Cookie') ||
            firstArg.includes('Partitioned') ||
            firstArg.includes('storage') ||
            firstArg.includes('Resource')) {
          return true;
        }
      }
      
      // # Check if it's an object that might contain a URL
      if (firstArg && typeof firstArg === 'object') {
        // # Convert to string to check for URLs (but don't log it)
        const objString = JSON.stringify(firstArg);
        if (objString.includes('notion.site') || 
            objString.includes('http') ||
            objString.includes('url') ||
            objString.includes('src')) {
          return true;
        }
      }
      
      return false;
    };
    
    // # Override console methods to filter URL-exposing messages
    console.error = function() {
      if (filterUrlMessages(arguments)) return;
      return originalConsoleError.apply(console, arguments);
    };
    
    console.warn = function() {
      if (filterUrlMessages(arguments)) return;
      return originalConsoleWarn.apply(console, arguments);
    };
    document.addEventListener('contextmenu', function(e) {
    e.preventDefault();
});
    console.log = function() {
      if (filterUrlMessages(arguments)) return;
      return originalConsoleLog.apply(console, arguments);
    };
    
    // # Create a function to handle messages from the iframe
    const handleMessage = (event) => {
      // # Block all messages from iframes to prevent URL leakage
      if (event.source !== window) {
        event.stopPropagation();
        return false;
      }
    };
    
    // # Add event listeners
    window.addEventListener('message', handleMessage, true); // Use capture phase
    window.addEventListener('error', (e) => {
      // # Prevent error events that might contain URLs
      if (e && e.message && (
          e.message.includes('http') || 
          e.message.includes('notion') ||
          e.message.includes('Cookie') ||
          e.message.includes('Partitioned'))) {
        e.preventDefault();
        e.stopPropagation();
        return true; // Prevent default error handling
      }
    }, true);
    
    // # Call the load handler when the component mounts
    if (iframeRef.current) {
      handleIframeLoad();
    }
    
    // # Cleanup function
    return () => {
      window.removeEventListener('message', handleMessage, true);
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      console.log = originalConsoleLog;
    };
  }, [iframeUrl, isDarkMode]);

  // # Handle missing URL with debug info
  if (!iframeUrl) {
    return (
      <div className={`mn_smpl_samples-page ${isDarkMode ? 'mn_smpl_dark-mode' : ''}`}>
        {isAuthenticated ? (
          <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        ) : (
          <Header />
        )}
        <main className="mn_smpl_samples-content">
          <div className="mn_smpl_coming-soon-message">
            <h2>Coming Soon!</h2>
            <p>Sample content is being prepared.</p>
          </div>
        </main>
      </div>
    );
  }

  // # Render iframe with content
  return (
    <div className={`mn_smpl_samples-page ${isDarkMode ? 'mn_smpl_dark-mode' : ''}`}>
      {isAuthenticated ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}
      <main className="mn_smpl_samples-content">
        {/* Error suppressor element */}
    
        <div className="mn_smpl_error-suppressor" aria-hidden="true"></div>
        
        <div className={`mn_smpl_iframe-container ${isDarkMode ? 'mn_smpl_dark-mode-container' : ''}`}>
          
          <iframe 
            ref={iframeRef}
            src={`${iframeUrl}?theme=${isDarkMode ? 'dark' : 'light'}&disableDatabase=true&disableSqlite=true&disableStorage=true&disableCookies=true`}
            title="Sample Content"
            width="100%" 
            height="100%" 
            frameBorder="0" 
            allowFullScreen
            referrerPolicy="no-referrer"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            loading="lazy"
            importance="high"
            className={isDarkMode ? 'mn_smpl_dark-mode-iframe' : 'mn_smpl_light-mode-iframe'}
            data-theme={isDarkMode ? 'dark' : 'light'}
            style={{
              colorScheme: isDarkMode ? 'dark' : 'light',
              backgroundColor: isDarkMode ? '#121212' : 'white'
            }}
            onLoad={handleIframeLoad}
          />
        </div>
      </main>
    </div>
  );
}

export default Samples; 